import { coreCall } from './gatewayService';

/**
 * Da offerta a ordini di magazzino.
 *
 * Unico punto del frontend che sa come si chiede la trasformazione. Ci si arriva
 * da due schermate — il riquadro nel dettaglio offerta e l'elenco delle offerte
 * in attesa dentro il magazzino — e devono comportarsi allo stesso modo.
 *
 * La regola vera (quali righe entrano, quanto resta da ordinare di ogni riga,
 * come si calcola l'IVA, che numero prende l'ordine) vive tutta in flusia-core
 * `orders/repository.ts` e `orders/dealAllocation.ts`. Qui non si duplica nulla
 * di quella logica: si chiama e si traduce la risposta.
 *
 * Un'offerta puo' essere divisa in piu' ordini scegliendo righe e quantita'.
 */

export interface CreatedOrderSummary {
  id: string;
  order_number: string;
}

export interface CreateOrderFromDealOutcome {
  /** null solo quando `skipped` e' true */
  order: CreatedOrderSummary | null;
  /** L'offerta non ha righe prodotto utilizzabili: non e' stato creato niente */
  skipped: boolean;
  /** Non restava nulla da ordinare: restituito l'ordine esistente, non ricreato */
  alreadyExisted: boolean;
  /** Le righe sono state aggiunte a un ordine in bozza esistente */
  addedToExisting: boolean;
  /** Righe dell'offerta non finite nell'ordine (i servizi) */
  skippedLinesCount: number;
}

export interface DealOrderLineSelection {
  deal_service_id: string;
  quantity: number;
}

export interface DealOrderLine {
  deal_service_id: string;
  product_id: string;
  variation_id: string | null;
  product_name: string;
  product_sku: string | null;
  unit_of_measure: string | null;
  unit_price: number;
  /** Quantita' nell'offerta */
  quantity: number;
  /** Gia' negli ordini non annullati */
  ordered: number;
  /** Ancora da ordinare */
  remaining: number;
  /** order_id -> quantita' */
  by_order: Record<string, number>;
  /** In DDT in uscita spediti o consegnati; assente con un flusia-core precedente */
  shipped?: number;
  /** In DDT in uscita consegnati; assente con un flusia-core precedente */
  delivered?: number;
}

export interface DealLinkedOrder {
  id: string;
  order_number: string;
  status: string;
  total: number | null;
  order_date: string | null;
}

export interface DealOrderLinesData {
  lines: DealOrderLine[];
  orders: DealLinkedOrder[];
  /** Massimo di ordini non annullati per l'offerta: uno per unità da distribuire */
  maxOrders: number;
}

/** Evasione dell'offerta: none = da evadere, partial = parzialmente evasa, full = evasa */
export type DealFulfillment = 'none' | 'partial' | 'full';

export interface DealPendingSummary {
  deal_id: string;
  product_lines: number;
  remaining_lines: number;
  orders_count: number;
  // Assenti se flusia-core non e' ancora aggiornato
  ordered_lines?: number;
  fulfillment?: DealFulfillment | null;
  total_value?: number;
  ordered_value?: number;
}

/**
 * Stato di evasione di un'offerta; null = niente da evadere (nessuna riga prodotto).
 * Il calcolo vero e' in flusia-core; il ripiego serve solo con un core precedente.
 */
export function dealFulfillmentState(summary: DealPendingSummary | undefined): DealFulfillment | null {
  if (!summary || summary.product_lines === 0) return null;
  if (summary.fulfillment !== undefined) return summary.fulfillment;
  if (summary.orders_count === 0) return 'none';
  return summary.remaining_lines === 0 ? 'full' : 'partial';
}

/**
 * Riepilogo di evasione a partire dalle righe gia' caricate nel dettaglio
 * offerta (`loadDealOrderLines`), nella stessa forma di `orders/deals-pending`:
 * cosi' il badge nel dettaglio dice esattamente quello che dice la lista.
 * Stessa regola di flusia-core `getDealsPendingSummary`.
 */
export function summaryFromDealLines(dealId: string, data: DealOrderLinesData): DealPendingSummary {
  const EPS = 0.0005;
  const round = (v: number) => Math.round(v * 100) / 100;
  const lines = data.lines;
  const remainingLines = lines.filter((l) => l.remaining > EPS).length;
  const orderedLines = lines.filter((l) => l.ordered > EPS).length;
  return {
    deal_id: dealId,
    product_lines: lines.length,
    remaining_lines: remainingLines,
    orders_count: data.orders.filter((o) => o.status !== 'cancelled').length,
    ordered_lines: orderedLines,
    fulfillment: lines.length === 0 ? null : orderedLines === 0 ? 'none' : remainingLines === 0 ? 'full' : 'partial',
    total_value: round(lines.reduce((acc, l) => acc + l.unit_price * l.quantity, 0)),
    ordered_value: round(lines.reduce((acc, l) => acc + l.unit_price * Math.min(l.ordered, l.quantity), 0)),
  };
}

export async function createOrderFromDeal(
  dealId: string,
  organizationId: string,
  options?: { lines?: DealOrderLineSelection[]; targetOrderId?: string },
): Promise<CreateOrderFromDealOutcome> {
  const { data, error } = await coreCall('orders/from-deal', {
    body: {
      deal_id: dealId,
      organization_id: organizationId,
      ...(options?.lines ? { lines: options.lines } : {}),
      ...(options?.targetOrderId ? { target_order_id: options.targetOrderId } : {}),
    },
  });

  if (error) throw new Error(error.message);

  if (data?.skipped) {
    return { order: null, skipped: true, alreadyExisted: false, addedToExisting: false, skippedLinesCount: 0 };
  }

  return {
    order: data?.order ? { id: data.order.id, order_number: data.order.order_number } : null,
    skipped: false,
    alreadyExisted: Boolean(data?.already_existed),
    addedToExisting: Boolean(data?.added_to_existing),
    skippedLinesCount: Number(data?.skipped_lines_count ?? 0),
  };
}

/** Righe prodotto dell'offerta con ordinato/residuo e ordini collegati */
export async function loadDealOrderLines(dealId: string, organizationId: string): Promise<DealOrderLinesData> {
  const { data, error } = await coreCall('orders/deal-lines', {
    body: { deal_id: dealId, organization_id: organizationId },
  });
  if (error) throw new Error(error.message);
  return {
    lines: (data?.lines ?? []) as DealOrderLine[],
    orders: (data?.orders ?? []) as DealLinkedOrder[],
    maxOrders: Math.max(1, Number(data?.max_orders ?? 1)),
  };
}

export interface DealOrderPlanEntry {
  /** Ordine in bozza esistente dell'offerta; assente = ordine nuovo */
  order_id?: string;
  lines: DealOrderLineSelection[];
}

export interface DealOrderPlanResult {
  created: CreatedOrderSummary[];
  updated: CreatedOrderSummary[];
}

/**
 * Divide l'offerta in piu' ordini in un colpo solo: crea gli ordini nuovi e
 * riscrive le righe degli ordini in bozza indicati. Validazione in flusia-core.
 */
export async function applyDealOrderPlan(
  dealId: string,
  organizationId: string,
  orders: DealOrderPlanEntry[],
): Promise<DealOrderPlanResult> {
  const { data, error } = await coreCall('orders/deal-plan', {
    body: { deal_id: dealId, organization_id: organizationId, orders },
  });
  if (error) throw new Error(error.message);
  return {
    created: (data?.created ?? []) as CreatedOrderSummary[],
    updated: (data?.updated ?? []) as CreatedOrderSummary[],
  };
}

/** Messaggio dopo la divisione in ordini */
export function messaggioPiano(result: DealOrderPlanResult): string {
  const parti: string[] = [];
  if (result.created.length === 1) parti.push(`Ordine ${result.created[0].order_number} creato`);
  else if (result.created.length > 1) parti.push(`Ordini ${result.created.map((o) => o.order_number).join(', ')} creati`);
  if (result.updated.length === 1) parti.push(`ordine ${result.updated[0].order_number} aggiornato`);
  else if (result.updated.length > 1) parti.push(`ordini ${result.updated.map((o) => o.order_number).join(', ')} aggiornati`);
  if (parti.length === 0) return 'Nessuna modifica agli ordini';
  const testo = parti.join(', ');
  return testo.charAt(0).toUpperCase() + testo.slice(1);
}

/** Per piu' offerte: quante righe prodotto restano da ordinare */
export async function loadDealsPendingSummary(
  organizationId: string,
  dealIds: string[],
): Promise<Map<string, DealPendingSummary>> {
  const { data, error } = await coreCall('orders/deals-pending', {
    body: { organization_id: organizationId, deal_ids: dealIds },
  });
  if (error) throw new Error(error.message);
  return new Map(((data?.deals ?? []) as DealPendingSummary[]).map((d) => [d.deal_id, d]));
}

/**
 * «Aggiorna dall'offerta»: riallinea solo le righe dell'offerta che
 * appartengono a questo ordine in bozza.
 */
export async function refreshOrderFromDeal(
  orderId: string,
  organizationId: string,
): Promise<{ itemsCount: number; removedCount: number; unassignedLines: number }> {
  const { data, error } = await coreCall('orders/refresh-from-deal', {
    body: { order_id: orderId, organization_id: organizationId },
  });
  if (error) throw new Error(error.message);
  return {
    itemsCount: Number(data?.items_count ?? 0),
    removedCount: Number(data?.removed_count ?? 0),
    unassignedLines: Number(data?.unassigned_lines ?? 0),
  };
}

/**
 * Il messaggio da mostrare dopo una trasformazione riuscita.
 * Sta qui perche' le due schermate devono dire la stessa cosa.
 */
export function messaggioEsito(outcome: CreateOrderFromDealOutcome): string {
  const numero = outcome.order?.order_number ?? '';

  if (outcome.alreadyExisted) {
    return `Tutte le righe di questa offerta sono gia' nell'ordine ${numero}`;
  }
  if (outcome.addedToExisting) {
    return `Righe aggiunte all'ordine ${numero}`;
  }

  const scartate = outcome.skippedLinesCount;
  if (scartate > 0) {
    return `Ordine ${numero} creato — ${scartate} ${scartate === 1 ? 'riga servizio non inclusa' : 'righe servizio non incluse'}`;
  }
  return `Ordine ${numero} creato`;
}
